import { PORTABLE_RECORDING_REF_PATTERN } from "@rekonas/domain";
import { authorizedFetch, parseResponse } from "./api";
import { authCacheScope } from "./cacheScope";

export interface AnalysisRelease {
  release_id: string;
  display_name?: string;
  algorithm_id: string;
  runtime?: { distribution: string; version: string };
  weights?: { distribution: string; version: string };
}
export interface AnalysisRun {
  run_ref: string;
  recording_ref: string;
  analysis_kind: string;
  input_data_version: string;
  input_manifest_sha256: string;
  algorithm_release: AnalysisRelease;
  parameters: Record<string, unknown>;
  configuration: unknown;
  status: "queued" | "running" | "complete" | "failed" | "unsupported_mapping";
  created: string;
  finished_at?: string;
  failure?: { code: string; retryable: boolean } | null;
  output?: { type: string; result_id?: string; data_version?: string; manifest_sha256?: string } | null;
  compatible: boolean;
}
export interface AnalysisHistory {
  data_version: string | null;
  duration_seconds: number | null;
  compatible_eeg: boolean;
  current: { run: AnalysisRun; compatible: boolean } | null;
  runs: AnalysisRun[];
  releases: AnalysisRelease[];
}
export interface AnalysisDefault {
  workspace_id: string;
  workspace_ref: string;
  release_id: string | null;
  revision: string;
  can_manage: boolean;
  built_in_release_id: string;
  releases: AnalysisRelease[];
  configured_available: boolean;
}
export type AnalysisDefaultChangeResult = Pick<AnalysisDefault,
  "workspace_id" | "workspace_ref" | "release_id" | "revision" | "can_manage"> & { audit_id?: string };
export const analysisKeys = {
  recordings: (workspace: string) => ["analysis-recordings", authCacheScope(), workspace] as const,
  history: (recording: string) => ["analysis-runs", authCacheScope(), recording] as const,
  default: (workspace: string) => ["analysis-default", authCacheScope(), workspace] as const,
};
function defaultRoute(workspace: string) {
  return `/platform/mobile/v1/analysis-default/${encodeURIComponent(workspace)}`;
}
export async function getAnalysisDefault(workspace: string, signal?: AbortSignal): Promise<AnalysisDefault> {
  return parseResponse(await authorizedFetch(defaultRoute(workspace), { signal }));
}
export async function setAnalysisDefault(workspace: string, releaseId: string | null, revision: string): Promise<AnalysisDefaultChangeResult> {
  return parseResponse(await authorizedFetch(defaultRoute(workspace), {
    method: "PUT", body: JSON.stringify({ release_id: releaseId, revision }),
  }));
}
function route(recording: string, suffix = "") {
  if (!PORTABLE_RECORDING_REF_PATTERN.test(recording)) throw new TypeError("Invalid recording reference");
  return `/platform/mobile/v1/recordings/${encodeURIComponent(recording)}/analyses${suffix}`;
}
export async function listAnalysisRecordings(workspace: string, signal?: AbortSignal): Promise<{ recording_ref: string; data_version: string | null; display_date: string | number | null; duration_seconds: number | null }[]> {
  return parseResponse(await authorizedFetch(`/platform/mobile/v1/analysis-recordings/${encodeURIComponent(workspace)}`, { signal }));
}
export async function listAnalysisRuns(recording: string, signal?: AbortSignal): Promise<AnalysisHistory> {
  return parseResponse(await authorizedFetch(route(recording), { signal }));
}
export async function requestAnalysisRun(recording: string, releaseId: string): Promise<AnalysisRun> {
  return parseResponse(await authorizedFetch(route(recording), { method: "POST", body: JSON.stringify({ release_id: releaseId, parameters: {} }) }));
}
export async function promoteAnalysisRun(recording: string, run: string): Promise<unknown> {
  return parseResponse(await authorizedFetch(route(recording, `/${encodeURIComponent(run)}/promote`), { method: "POST", body: "{}" }));
}
export async function getAnalysisResult(recording: string, run: string, signal?: AbortSignal): Promise<unknown> {
  return parseResponse(await authorizedFetch(route(recording, `/${encodeURIComponent(run)}/result`), { signal }));
}
